import React from "react";
import { subscribeToMailchimp } from "../../../lib/mailchimpRequests";
import Link from "next/link";
import { useRouter } from "next/router";
import { ModalContext } from "../../../context/ModalContext";
import { useAuth } from "../../../context/authContext";
import Button from "../Buttons/Button";

const MAILCHIMP_HOSTED_FORM_URL =
  "https://vinousmedia.us7.list-manage.com/subscribe?u=1b0468fdd47cc4ecb9d11de24&id=965624e71b";
const USE_MAILCHIMP_TEMPLATE = false;
const NEWSLETTER_POPUP_DISMISSED_KEY = "vinous_newsletter_popup_dismissed";
// Survives full page loads within the tab, so a plain <a href> to another page
// doesn't show the popup a second time. A browser reload ignores it.
const NEWSLETTER_POPUP_SHOWN_KEY = "vinous_newsletter_popup_shown";

const isBrowserReload = () => {
  const [navigation] = window.performance?.getEntriesByType?.("navigation") ?? [];
  return (navigation as PerformanceNavigationTiming | undefined)?.type === "reload";
};
const POPUP_DELAY_MS = 2500;
const POPUP_EXCLUDED_PATH_PREFIXES = [
  "/users",
  "/admin",
  "/capitalone_enter",
  "/statics/privacy",
  "/statics/terms_and_conditions",
];

const NewsletterPopupContent = ({ onDismiss }: { onDismiss: () => void }) => {
  const [email, setEmail] = React.useState("");
  const [submitStatus, setSubmitStatus] = React.useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = React.useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // validate email format email regex and check if email is empty
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setMessage("Please enter a valid email address.");
      setSubmitStatus("error");
      return;
    }

    try {
      setSubmitStatus("submitting");
      setMessage("");
      await subscribeToMailchimp(email);
      setSubmitStatus("success");
    } catch (err: any) {
      const errorMessage =
        err?.err?.message ||
        err?.data?.message ||
        err?.message ||
        err?.data?.error
        "Failed to subscribe to newsletter. Please try again later.";
      setMessage(errorMessage);
      setSubmitStatus("error");
    }
  };

  if (USE_MAILCHIMP_TEMPLATE) {
    return (
      <div className="p-4 md:p-6 bg-white">
        <iframe
          title="Vinous newsletter signup"
          src={MAILCHIMP_HOSTED_FORM_URL}
          className="h-[620px] w-full rounded-lg border border-gray-200"
        />
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600">
          <Link
            href={MAILCHIMP_HOSTED_FORM_URL}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Open form in a new tab
          </Link>
          <button type="button" className="underline" onClick={onDismiss}>
            I subscribed
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-white shadow-xl">
      <div className="flex flex-col md:flex-row">
        <div className="relative h-56 md:h-auto md:w-[42%] lg:w-[45%]">
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('/img/vinous-newsletter.webp')" }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-black/5 to-transparent" />

          <svg
            aria-hidden="true"
            className="pointer-events-none absolute -right-px top-0 hidden h-full w-12 text-white md:block"
            viewBox="0 0 160 1440"
            preserveAspectRatio="none"
            fill="currentColor"
          >
            <path d="M32,0 C160,480 0,960 96,1440 L160,1440 L160,0 Z" />
          </svg>

          <svg
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-px left-0 h-12 w-full text-white md:hidden"
            viewBox="0 0 1440 160"
            preserveAspectRatio="none"
            fill="currentColor"
          >
            <path d="M0,32 C480,160 960,0 1440,96 L1440,160 L0,160 Z" />
          </svg>
        </div>

        <div className="relative md:w-[58%] lg:w-[55%]">
          <div className="relative px-6 pb-7 pt-6 md:px-10 md:pb-10 md:pt-8">
            {submitStatus === "success" ? (
              <div className="flex flex-col items-center py-6 text-center md:py-10">
                <img
                  src="/img/vinous-logo.png"
                  alt="Vinous"
                  className="h-14 w-auto"
                />
                <h2 className="mt-6 text-3xl font-semibold leading-tight text-black md:text-4xl">
                  Thank you!
                </h2>
                <p className="mt-3 text-sm leading-6 text-gray-700 md:text-base">
                  Stay tuned for updates from Vinous
                </p>
                <button
                  type="button"
                  className="mt-7 inline-flex items-center justify-center rounded-md bg-black px-6 py-3 text-sm font-semibold text-white transition hover:bg-gray-800"
                  onClick={onDismiss}
                >
                  Back to site
                </button>
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  <img
                    src="/img/vinous-logo.png"
                    alt="Vinous"
                    className="h-14 w-auto"
                  />
                </div>
                {/* Center h2 and p as same as logo*/}
                <div className="flex flex-col items-center text-center">
                  <h2 className="mt-6 text-2xl font-semibold leading-tight text-black md:text-3xl">
                    Explore all things wine
                  </h2>
                  <p className="mt-3 text-sm leading-6 text-gray-700 md:text-base">
                    Stay up-to-date on all the latest articles, reviews and ratings
                    from Vinous.
                  </p>
                </div>

                <form
                  className="mt-6"
                  onSubmit={handleSubmit}
                >
                  <label htmlFor="newsletter-email" className="sr-only">
                    Email
                  </label>
                  <input
                    id="newsletter-email"
                    name="EMAIL"
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value)
                      setSubmitStatus("idle")
                      setMessage("")
                    }}
                    className="w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-black focus:ring-2 focus:ring-gray-200"
                  />

                  {submitStatus === "error" && (
                     <div className="border !border-red-400 rounded !bg-red-100 px-4 py-3 text-red-700 mb-4 mt-2" role="alert">
                        <p>{message}</p>
                      </div>
                  )}

                   <div className="mt-4 flex flex-col gap-3 items-center ">
                    <Button
                      type="secondary"
                      label="Stay up to date"
                      labelColor="italic font-normal"
                      className="w-full xs:w-full md:w-full lg:w-full xl:w-full 2xl:w-1/2"
                      isLoading={submitStatus === "submitting"}
                      onClick={handleSubmit}
                    ></Button>
                    <Button
                      label="Sign into Vinous"
                      type="default"
                      className="w-full xs:w-full md:w-full lg:w-full xl:w-full 2xl:w-1/2"
                      onClick={() => (window.location.href = "/users/sign_in")}
                    ></Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const NewsletterPopup = () => {
  const router = useRouter();
  const {
    state: { user, initialLoad },
  } = useAuth();
  const { modal, handleModal, closeModal } = React.useContext(ModalContext) as unknown as {
    modal: boolean;
    handleModal: (modalContent?: JSX.Element) => void;
    closeModal: () => void;
  };
  const popupOpenedRef = React.useRef(false);
  const newsletterModalOpenRef = React.useRef(false);
  const lastPathRef = React.useRef<string | null>(null);

  const dismissPopup = React.useCallback(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(NEWSLETTER_POPUP_DISMISSED_KEY, "true");
    }
    newsletterModalOpenRef.current = false;
    closeModal();
  }, [closeModal]);

  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }

    const currentPath = router.asPath;

    if (lastPathRef.current === null) {
      lastPathRef.current = currentPath;
      return;
    }

    if (
      lastPathRef.current !== currentPath &&
      modal &&
      newsletterModalOpenRef.current
    ) {
      newsletterModalOpenRef.current = false;
      handleModal();
    }

    lastPathRef.current = currentPath;
  }, [handleModal, modal, router.asPath, router.isReady]);

  React.useEffect(() => {
    if (
      !router.isReady ||
      user ||
      !initialLoad ||
      modal ||
      popupOpenedRef.current
    ) {
      return;
    }

    const currentPath = router.asPath.split("?")[0].replace(/\/$/, "") || "/";

    const shouldSkipRoute = POPUP_EXCLUDED_PATH_PREFIXES.some((pathPrefix) =>
      currentPath.startsWith(pathPrefix)
    );

    if (shouldSkipRoute || typeof window === "undefined") {
      return;
    }

    try {
      if (
        window.localStorage.getItem(NEWSLETTER_POPUP_DISMISSED_KEY) === "true" ||
        (window.sessionStorage.getItem(NEWSLETTER_POPUP_SHOWN_KEY) === "true" &&
          !isBrowserReload())
      ) {
        return;
      }
    } catch {
      // Storage can be unavailable (e.g. blocked site data); fall back to the ref.
    }

    const timeoutId = window.setTimeout(() => {
      popupOpenedRef.current = true;
      try {
        window.sessionStorage.setItem(NEWSLETTER_POPUP_SHOWN_KEY, "true");
      } catch {}
      newsletterModalOpenRef.current = true;
      handleModal(<NewsletterPopupContent onDismiss={dismissPopup} />);
    }, POPUP_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    dismissPopup,
    handleModal,
    initialLoad,
    modal,
    router.asPath,
    router.isReady,
    user,
  ]);

  return null;
};

export default NewsletterPopup;
